/**
 * Tenant ownership — domain rules, deliberately OUTSIDE the permission system.
 *
 * Ownership is a singular property of the tenant (`tenants.owner_user_id`), not
 * a grantable capability, so the right to reassign it is NOT modelled as a
 * `can()` permission. Its own domain rule governs it:
 *
 *   A caller may transfer ownership iff they are
 *     - a super_user (who may also hand ownership to ANY user, including
 *       another super_user), OR
 *     - the tenant's current owner (regardless of their role — a viewer who
 *       owns the tenant can still hand it off).
 *
 * The target may be any active user belonging to the tenant, of any role —
 * role hierarchy does NOT constrain ownership transfer. Cross-tenant scoping
 * (non-super-users confined to their own tenant) is a separate guard enforced
 * at the route layer.
 *
 * Isomorphic: the structural caller shape accepts both the server's
 * `UserAuthData` and the client's `AuthUser`, so route and UI share one rule.
 */

export interface OwnershipCaller {
  user_id: string
  role?: string
  isSuperUser?: boolean
}

export interface OwnedTenant {
  owner_user_id: string | null
}

export function canTransferOwnership(caller: OwnershipCaller, tenant: OwnedTenant): boolean {
  if (caller.isSuperUser || caller.role === "super_user") return true
  return tenant.owner_user_id !== null && caller.user_id === tenant.owner_user_id
}
